import { useEffect } from "react";

/**
 * Drives every scroll-triggered animation on the site from one place.
 *
 * Components opt in declaratively by putting `data-reveal="rise"` (or "fade",
 * "rise-sm", "wipe") on an element. This hook watches the document for those
 * elements, and the first time one scrolls into view it stamps
 * `data-revealed="true"` on it. The actual animation is pure CSS — see the
 * "SCROLL REVEAL" block in `src/styles/base.css`.
 *
 * Two details make it robust:
 *
 *  1. A MutationObserver picks up elements React mounts later, so navigating
 *     to another route needs no extra wiring.
 *  2. If the visitor has asked their OS to reduce motion, or the browser has
 *     no IntersectionObserver, every element is marked revealed immediately.
 *     Content is never hidden behind an animation that will not run.
 *
 * Call once, from the application root.
 *
 * @returns {void}
 */
export function useScrollReveal() {
  useEffect(() => {
    const REVEAL_SELECTOR = "[data-reveal]:not([data-revealed])";

    /** @param {Element} element */
    const markAsRevealed = (element) => {
      element.setAttribute("data-revealed", "true");
    };

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const canObserve = typeof window.IntersectionObserver === "function";

    /* ----- Fallback path: show everything, skip the choreography ---------- */
    if (prefersReducedMotion || !canObserve) {
      const revealEverything = () => {
        document.querySelectorAll(REVEAL_SELECTOR).forEach(markAsRevealed);
      };
      revealEverything();

      const fallbackMutationObserver = new MutationObserver(revealEverything);
      fallbackMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => fallbackMutationObserver.disconnect();
    }

    /* ----- Normal path --------------------------------------------------- */
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          markAsRevealed(entry.target);
          // Reveals happen once; stop watching to keep the observer cheap.
          intersectionObserver.unobserve(entry.target);
        });
      },
      {
        // Trigger a little before the element reaches the bottom edge so the
        // animation is already underway when the visitor notices it.
        rootMargin: "0px 0px -10% 0px",
        threshold: 0.08,
      }
    );

    /**
     * Observe the node itself plus any reveal candidates inside it.
     * @param {Node} root
     */
    const observeTreeOf = (root) => {
      if (!(root instanceof Element) && root !== document) return;

      if (root instanceof Element && root.matches(REVEAL_SELECTOR)) {
        intersectionObserver.observe(root);
      }
      root
        .querySelectorAll(REVEAL_SELECTOR)
        .forEach((element) => intersectionObserver.observe(element));
    };

    observeTreeOf(document);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(observeTreeOf);
      });
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}

export default useScrollReveal;
